import { useLocalization, useUrl } from '@shopify/hydrogen'
import { LanguageCode } from '@shopify/hydrogen/storefront-api-types'
import { AnimatePresence, motion } from 'framer-motion'
import { Dispatch, MouseEvent, SetStateAction, useState, useEffect } from 'react'
import styles from '../../assets/scss/components/language/language-modal.module.scss'
import { useCookie } from '../../lib/hooks/cookie.client'
import { useI18n } from '../i18n.client'
import { languageOptions } from '../../lib/language'
import { clone } from 'lodash'

export const LanguageModal: React.FC<{ siteLanguageCode: LanguageCode }> = ({ siteLanguageCode }) => {
  const [i18n] = useI18n()
  const { getSessionCookie } = useCookie()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const siteLanguage = siteLanguageCode === LanguageCode.Fr ? i18n.translate('french') : i18n.translate('english')

  useEffect(() => {
    if (!getSessionCookie<boolean>('isPreferredLanguageSet')) {
      setIsModalOpen(true)
    }
  }, [])

  if (getSessionCookie<boolean>('isPreferredLanguageSet')) {
    return null
  }

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          animate={{ opacity: 1 }}
          className={styles.overlay}
          exit={{ opacity: 0, transition: { ease: [1, 0, 1, 1] } }}
          initial={{ opacity: 0 }}
          transition={{ ease: [0, 0, 0, 1] }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.background}>
              <div className={styles.contents}>
                <div className={styles.title}>{i18n.translate('welcome-to-marie-lichtenberg')}</div>
                <div className={styles.description}>
                  {i18n.translate('you-are-visiting-marie-lichtenberg-in-language', { language: siteLanguage })}
                </div>
                <CountryOptions reverseOptions={siteLanguageCode !== LanguageCode.Fr} setIsModalOpen={setIsModalOpen} />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const CountryOptions: React.FC<{
  reverseOptions: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
}> = ({ reverseOptions = false, setIsModalOpen }) => {
  const [i18n] = useI18n()
  const { setSessionCookie } = useCookie()
  const {
    language: { isoCode: language },
    country: { isoCode: country },
  } = useLocalization()

  const { pathname } = useUrl()
  const sanitisedPathname = pathname.replace(/^\/([a-z]{2})(-([a-z]{2}))?(\/|$)/i, '/')

  let options = clone(languageOptions)

  if (reverseOptions) {
    options = options.reverse()
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, url: string) => {
    if (`/${language.toLowerCase()}-${country.toLowerCase()}` === url) {
      event.preventDefault()
      setIsModalOpen(false)
    }

    setSessionCookie('isPreferredLanguageSet', true)
  }

  return (
    <div>
      {options.map(({ title, url }) => (
        <a
          className={styles.button}
          href={`${url}${sanitisedPathname}`}
          key={url}
          onClick={(event) => handleClick(event, url)}>
          {i18n.translate(title)}
        </a>
      ))}
    </div>
  )
}
